import { useMemo } from 'react';
import { contextualParams, getApiClientErrorMessageFromError, hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { AccountFeatureFlag } from '@/services/auth/feature-flags';
import type { LocalCommand } from './local-commands';
import { matchesLocalCommand } from './utils/search-commands';

const DRYAD_FLAGS = Object.values(AccountFeatureFlag).filter((flag) => flag.startsWith('dryad-'));

// Same local-only gate as the dev-helper's debug tabs.
const isLocalEnv = Boolean(import.meta.env.DEV) || import.meta.env.VITE_ENABLE_EXTERNAL_UI_OVERRIDES === 'true';

async function setAllDryadFlags(
  accountId: string,
  currentFlags: Record<string, boolean>,
  enabled: boolean,
): Promise<void> {
  const dryadFlags = Object.fromEntries(DRYAD_FLAGS.map((flag) => [flag, enabled]));
  await hydraUiApi.accounts.updateFeatureFlags({
    params: { path: { account_id: accountId } },
    body: { ...currentFlags, ...dryadFlags },
  });
  window.location.reload();
}

type DevCommandsOptions = {
  includeEmptyQuery?: boolean;
};

export function useDevCommands(query: string, options: DevCommandsOptions = {}): LocalCommand[] {
  const { includeEmptyQuery = false } = options;
  const params = contextualParams();

  const { data: featureFlags } = useHydraUIAPIQuery({
    queryKey: ['getFeatureFlags', params.accountId],
    queryFn: ({ signal }) =>
      hydraUiApi.accounts.getFeatureFlags({ params: { path: { account_id: String(params.accountId!) } }, signal }),
    queryConfig: {
      enabled: isLocalEnv && params.accountId != null,
      select: (data) => data.featureFlags ?? {},
    },
  });

  const accountId = params.accountId;

  return useMemo(() => {
    if (!isLocalEnv || accountId == null) return [];
    if (!includeEmptyQuery && query.trim().length === 0) return [];
    // The PUT body must carry the full current flag map — without it we could
    // wipe every non-dryad flag. No command until the flags query resolves.
    if (featureFlags == null) return [];

    const allEnabled = DRYAD_FLAGS.every((flag) => featureFlags[flag] === true);

    const commands: LocalCommand[] = [
      {
        id: 'dev:toggle-dryad-globally',
        label: allEnabled ? 'Disable Dryad globally' : 'Enable Dryad globally',
        icon: 'skills',
        description: allEnabled
          ? 'Turn every registered dryad feature flag off and reload'
          : 'Turn every registered dryad feature flag on and reload',
        keywords: ['dryad', 'enable', 'disable', 'toggle', 'feature', 'flags', 'condition', 'builder', 'dev'],
        onSelect: async () => {
          try {
            await setAllDryadFlags(String(accountId), featureFlags, !allEnabled);
          } catch (error) {
            // Dev-only tool; alert avoids wiring snackbars outside the provider tree.
            window.alert(`Failed to update dryad flags: ${getApiClientErrorMessageFromError(error) ?? String(error)}`);
          }
        },
      },
    ];

    return commands.filter(matchesLocalCommand(query));
  }, [accountId, featureFlags, includeEmptyQuery, query]);
}
